import cn from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';
import DotButton, { useDotButton } from './DotButton';
import ClassNames from 'embla-carousel-class-names';
import Autoplay from 'embla-carousel-autoplay';
import { EmblaOptionsType } from 'embla-carousel';
import {
  NextButton,
  PrevButton,
  usePrevNextButton,
} from './NavButton';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

type Breakpoint = 'base' | 'sm' | 'md' | 'lg' | 'xl';
type BreakpointConfig = Partial<Record<Breakpoint, number>> & {
  base: number;
};

interface CarouselProps {
  className?: string;
  slidesToShow: BreakpointConfig;
  spacing?: BreakpointConfig;
  snappedSize?: number;
  snapped?: boolean;
  dots?: boolean;
  nav?: boolean;
  navButtonClassName?: string;
  autoplay?: boolean;
  delay?: number;
  children: React.ReactNode;
  options?: EmblaOptionsType;
  wheelGesture?: boolean;
}

const BREAKPOINTS: Record<Exclude<Breakpoint, 'base'>, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

const BREAKPOINT_ORDER: Breakpoint[] = [
  'base',
  'sm',
  'md',
  'lg',
  'xl',
];

const normalizeBreakpoints = (
  config: BreakpointConfig,
): Record<Breakpoint, number> => {
  const normalized: Record<Breakpoint, number> = {} as any;
  let last = config.base ?? 1;
  for (const bp of BREAKPOINT_ORDER) {
    if (config[bp] !== undefined) last = config[bp]!;
    normalized[bp] = last;
  }
  return normalized;
};

const Carousel = ({
  className,
  slidesToShow,
  spacing = { base: 1.5 },
  nav,
  dots = true,
  autoplay,
  delay = 3000,
  snapped,
  snappedSize = 30,
  children,
  options,
  wheelGesture,
}: CarouselProps) => {
  const { base: defaultSpacing } = spacing ?? {};
  const [currentSlidesToShow, setCurrentSlidesToShow] = useState(
    slidesToShow.base,
  );
  const [currentSpacing, setCurrentSpacing] = useState(
    defaultSpacing,
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    ...(snapped ? [ClassNames()] : []),
    ...(autoplay ? [Autoplay({ playOnInit: true, delay })] : []),
    ...(wheelGesture ? [WheelGesturesPlugin()] : []),
  ]);

  const {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  } = useDotButton(emblaApi);

  const {
    onNextButtonClick,
    onPrevButtonClick,
    prevBtnDisabled,
    nextBtnDisabled,
  } = usePrevNextButton(emblaApi);

  const normalizedSlides = normalizeBreakpoints(slidesToShow);
  const normalizedSpacing = normalizeBreakpoints(spacing);

  const width =
    typeof window !== 'undefined'
      ? window.innerWidth
      : BREAKPOINTS.sm;

  const updateResponsiveValues = useCallback(() => {
    if (typeof window === 'undefined') return;

    let slides = normalizedSlides.base;
    let space = normalizedSpacing.base;

    if (width >= BREAKPOINTS.xl) {
      slides = normalizedSlides.xl;
      space = normalizedSpacing.xl;
    } else if (width >= BREAKPOINTS.lg) {
      slides = normalizedSlides.lg;
      space = normalizedSpacing.lg;
    } else if (width >= BREAKPOINTS.md) {
      slides = normalizedSlides.md;
      space = normalizedSpacing.md;
    } else if (width >= BREAKPOINTS.sm) {
      slides = normalizedSlides.sm;
      space = normalizedSpacing.sm;
    }

    setCurrentSlidesToShow(slides);
    setCurrentSpacing(space);
  }, [normalizedSlides, normalizedSpacing, width]);

  useEffect(() => {
    updateResponsiveValues();
    window.addEventListener('resize', updateResponsiveValues);
    return () =>
      window.removeEventListener('resize', updateResponsiveValues);
  }, [updateResponsiveValues, slidesToShow, spacing]);

  let slideSize = 100 / currentSlidesToShow; // default
  if (snapped && width < BREAKPOINTS.md) {
    slideSize = 100 - snappedSize;
  }

  return (
    <div
      className={cn('tw-m-auto tw-relative embla', className)}
      style={nav ? { paddingLeft: '4rem', paddingRight: '4rem' } : {}}
    >
      <div
        className="tw-overflow-hidden embla__viewport"
        ref={emblaRef}
      >
        <div
          className={cn('tw-flex embla__container')}
          style={{
            marginLeft: `-${currentSpacing}rem`,
            touchAction: 'pan-y pinch-zoom',
          }}
        >
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              style={{
                minWidth: 0,
                transform: 'translate3d(0,0,0)',
                flex: `0 0 ${slideSize}%`,
                paddingLeft: `${currentSpacing}rem`,
                userSelect: 'none',
              }}
              className={cn('embla__slide')}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      {dots && (
        <div className="tw-flex tw-justify-center tw-items-center tw-mt-8">
          {dots && (
            <div className="tw-flex tw-flex-wrap tw-items-center">
              {scrollSnaps.map((_, index) => (
                <DotButton
                  key={index}
                  onClick={() => onDotButtonClick(index)}
                  className={cn(
                    'tw-appearance-none tw-bg-transparent tw-h-8 tw-w-8 tw-inline-flex tw-items-center tw-justify-center tw-cursor-pointer tw-touch-manipulation tw-transition-all tw-duration-200',
                    'after:tw-content-[""] after:tw-w-3.5 after:tw-h-3.5 after:tw-rounded-full after:tw-flex after:tw-items-center',
                    selectedIndex === index
                      ? 'after:tw-bg-primary'
                      : 'after:tw-bg-[#666]',
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {nav && (
        <>
          <PrevButton
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          />
          <NextButton
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          />
        </>
      )}
    </div>
  );
};

export default Carousel;
